// Pedaco da tela que sumiu depois de um deploy. Se foi numa troca de tela, o
// router.onError recarrega no DESTINO (routes/index.js); este recarregamento
// e so a rede pra import fora de rota, e espera o roteador agir primeiro.
window.addEventListener('vite:preloadError', () => {
  setTimeout(() => {
    if (!window.smartRecarregandoNoDestino) window.location.reload();
  }, 1500);
});

import { createApp, defineAsyncComponent } from 'vue';
import { createI18n } from 'vue-i18n';

import axios from 'axios';
// Global Components
import hljsVuePlugin from '@highlightjs/vue-plugin';

import Multiselect from 'vue-multiselect';
import { plugin, defaultConfig } from '@formkit/vue';
import WootWizard from 'components/ui/Wizard.vue';
import FloatingVue from 'floating-vue';
import WootUiKit from 'dashboard/components';
import App from 'dashboard/App.vue';
import i18nMessages from 'dashboard/i18n';
import createAxios from 'dashboard/helper/APIHelper';

import commonHelpers, { isJSONValid } from 'dashboard/helper/commons';
import { sync } from 'vuex-router-sync';
import { createPinia } from 'pinia';
import router, { initalizeRouter } from 'dashboard/routes';
import store from 'dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from 'dashboard/helper/scriptHelpers.js';
import VueDOMPurifyHTML from 'vue-dompurify-html';

const FluentIcon = defineAsyncComponent(
  () => import('shared/components/FluentIcon/DashboardIcon.vue')
);
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';

import { vResizeObserver } from '@vueuse/components';
import { directive as onClickaway } from 'vue3-click-away';

import 'floating-vue/dist/style.css';

const i18n = createI18n({
  legacy: false, // https://github.com/intlify/vue-i18n/issues/1902
  locale: 'en',
  messages: i18nMessages,
});

sync(store, router);

const pinia = createPinia();

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(pinia);
app.use(router);

if (window.sentryConfig?.dsn) {
  Sentry.init({
    app,
    dsn: window.sentryConfig.dsn,
    environment: window.sentryConfig.environment || 'production',
    tracesSampleRate: window.sentryConfig.tracesSampleRate ?? 0.1,
    denyUrls: [
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,
      /file:\/\//i,
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend(event) {
      if (event.request?.headers) {
        ['Authorization', 'Cookie', 'X-Auth-Token'].forEach(h => {
          if (event.request.headers[h]) event.request.headers[h] = '[FILTERED]';
        });
      }
      return event;
    },
  });
} else if (window.errorLoggingConfig) {
  // Fallback para compatibilidade com layout antigo
  Sentry.init({
    app,
    dsn: window.errorLoggingConfig,
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

app.use(VueDOMPurifyHTML, domPurifyConfig);
app.use(WootUiKit);
app.use(
  plugin,
  defaultConfig({
    rules: {
      JSON: ({ value }) => isJSONValid(value),
    },
  })
);
app.use(FloatingVue, {
  instantMove: true,
  arrowOverflow: false,
  disposeTimeout: 5000000,
});
app.use(hljsVuePlugin);

app.component('multiselect', Multiselect);
app.component('woot-wizard', WootWizard);
app.component('fluent-icon', FluentIcon);

app.directive('resize', vResizeObserver);
app.directive('on-clickaway', onClickaway);

// load common helpers into js
commonHelpers();
window.WootConstants = constants;
window.axios = createAxios(axios);
// [VITE] Disabled this we don't need it, we can use `useEmitter` directly
// app.prototype.$emitter = emitter;

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();

window.onload = () => {
  app.mount('#app');
};
